"use client"

import { Search, Loader2, ChevronDown, ArrowLeft } from "lucide-react"
import type { ClawHubSkill, SkillCategoryItem } from "@/lib/clawhub-api"
import { SkillCard } from "./skill-card"
import { SortDropdown, type SortValue } from "./sort-dropdown"

interface SkillsGridProps {
  skills: ClawHubSkill[]
  loading: boolean
  loadingMore: boolean
  total: number
  hasMore: boolean
  sortBy: SortValue
  onSortChange: (val: SortValue) => void
  onLoadMore: () => void
  onSkillClick: (skill: ClawHubSkill) => void
  onTagClick: (tagName: string) => void
  /** 左侧分类侧边栏数据，来自 /skill/api/v1/categories */
  categories: SkillCategoryItem[]
  selectedCategory: string
  onCategoryClick: (catId: string) => void
  /** 点击返回，切回 hero 模式 */
  onBack: () => void
  onSkillUpdate?: (updated: ClawHubSkill) => void
}

/**
 * SkillsGrid — search results grid with left category sidebar + sort + pagination.
 */
export function SkillsGrid({
  skills,
  loading,
  loadingMore,
  total,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onSkillClick,
  onTagClick,
  categories,
  selectedCategory,
  onCategoryClick,
  onBack,
  onSkillUpdate,
}: SkillsGridProps) {
  return (
    <div className="mx-auto max-w-[1200px] pb-8 flex flex-col gap-0">
      <div className="flex gap-5 items-start">
        {/* 左侧分类侧边栏 */}
        <aside className="hidden md:flex flex-col shrink-0 w-40 pt-1 sticky top-0">
          <p className="text-xs font-medium text-muted-foreground mb-2 px-2">分类</p>
          <nav className="flex flex-col gap-0.5">
            {categories.map((cat) => {
              const active = selectedCategory === cat.value
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => onCategoryClick(cat.value)}
                  className={[
                    "flex items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors text-left w-full",
                    active
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  ].join(" ")}
                >
                  <span className="truncate">{cat.label || cat.value}</span>
                  {cat.count > 0 && (
                    <span className={[
                      "ml-1.5 shrink-0 text-xs tabular-nums",
                      active ? "text-primary" : "text-muted-foreground/60",
                    ].join(" ")}>
                      {cat.count}
                    </span>
                  )}
                </button>
              )
            })}
          </nav>
        </aside>

        {/* 右侧内容区 */}
        <div className="flex-1 min-w-0">
          {/* Results count + sort */}
          <div className="flex items-center justify-between mb-4">
            <p className="skills-results-count">
              找到{" "}
              <span className="skills-results-count__num">{total}</span>{" "}
              个技能
            </p>
            <SortDropdown value={sortBy} onChange={onSortChange} />
          </div>

          {/* Loading state */}
          {loading ? (
            <div className="skills-loading">
              <Loader2 className="skills-loading__spinner animate-spin" />
              <p className="text-sm">加载中...</p>
            </div>
          ) : skills.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {skills.map((skill, i) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    onClick={() => onSkillClick(skill)}
                    onTagClick={onTagClick}
                    delay={i * 30}
                    onSkillUpdate={onSkillUpdate}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    className="skills-load-more-btn"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="skills-loading__spinner--sm animate-spin" />
                        加载中...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        加载更多
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="skills-empty-state">
              <Search className="skills-empty-state__icon" />
              <p className="text-sm">未找到匹配的技能</p>
              <p className="text-xs mt-1">尝试调整搜索关键词或分类</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
